import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  fetchProvidersByStatus,
  verifyProvider,
  rejectProvider,
} from "../../store/slice/admin/adminProviderThunks";

type StatusFilter = "PENDING" | "VERIFIED" | "REJECTED";

const AdminDashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { providers, loading, error } = useSelector(
    (state: RootState) => state.adminProvider
  );
  const [status, setStatus] = useState<StatusFilter>("PENDING");

  const handleLoad = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(fetchProvidersByStatus(status));
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-8">
      <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>

      {error && <p className="text-red-600">{error}</p>}

      <form onSubmit={handleLoad} className="flex items-end gap-3">
        <div className="flex-1">
          <label className="block mb-1 text-sm font-medium text-gray-900">
            Provider Status
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusFilter)}
            className="w-full p-2.5 text-sm rounded-lg border border-gray-300"
          >
            <option value="PENDING">Pending</option>
            <option value="VERIFIED">Verified</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="py-2.5 px-5 text-sm font-medium text-white rounded-lg bg-gray-700 hover:bg-gray-800 disabled:bg-gray-400"
        >
          {loading ? "Loading..." : "Load Providers"}
        </button>
      </form>

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Providers</h2>
        {providers.length === 0 ? (
          <p className="text-gray-500 text-sm">No providers loaded yet.</p>
        ) : (
          <ul className="space-y-2">
            {providers.map((p: any) => (
              <li
                key={p.id}
                className="border border-gray-200 rounded-lg p-3 flex items-center justify-between"
              >
                <div>
                  <p className="font-medium text-gray-900">
                    #{p.id} — {p.businessName || p.fullName}
                  </p>
                  <p className="text-sm text-gray-500">
                    {p.email} · {p.phone}
                  </p>
                  <p className="text-sm text-gray-500">
                    {p.service?.name} · {p.lga?.name}, {p.state?.name}
                  </p>
                  {p.idDocumentUrl && (
                    <a
                      href={p.idDocumentUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-green-600 hover:underline"
                    >
                      View ID document
                    </a>
                  )}
                </div>
                {status === "PENDING" && (
                  <div className="flex gap-2">
                    <button
                      disabled={loading}
                      onClick={() => dispatch(verifyProvider(p.id))}
                      className="py-1.5 px-3 text-sm font-medium text-white rounded-lg bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
                    >
                      Verify
                    </button>
                    <button
                      disabled={loading}
                      onClick={() => dispatch(rejectProvider(p.id))}
                      className="py-1.5 px-3 text-sm font-medium text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-gray-400"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
